import React, { useState, useEffect } from "react";
import Skeleton from "@material-ui/lab/Skeleton";
import { Box, makeStyles } from "@material-ui/core";
import { Link as RouterLink, Route } from "react-router-dom";
import { PhoneNumberUtil } from "google-libphonenumber";
import { useSnackbar } from "notistack";
import countries from "../../../utils/countries.json";
import ChangeEmail from "./ChangeEmail.js";
import API from "../../../utils/axios.js";
import BlackButton from "../BlackButton.js";
import Form from "../../common/Form";
import { validateName } from "../../../utils/helpers.js";

// Get an instance of `PhoneNumberUtil`.
const phoneUtil = PhoneNumberUtil.getInstance();

const useStyles = makeStyles(theme => ({
  animationWrapper: {
    backgroundColor: "#fff"
  },
  formWrapper: {
    overflow: "hidden",
    boxSizing: "border-box",
    transition: theme.transitions.create(["padding"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  },
  changeEmailLink: {
    color: theme.palette.primary.main,
    cursor: "pointer",
    textDecoration: "none !important",
    fontSize: "0.75rem",
    fontWeight: 500,
    marginTop: 3
  }
}));

export default function ProfileTab({
  userData,
  value,
  drawerExpanded,
  setLoading,
  setUser,
  saveDisabled,
  setSaveDisabled
}) {
  const classes = useStyles();

  const organisation = userData.organisationSite
    ? userData.organisationSite.organisation
    : null;

  const generateInputValues = () => ({
    firstName: (userData.fullName && userData.fullName.split(" ")[0]) || "",
    lastName:
      (userData.fullName && userData.fullName.split(" ").slice(1).join(" ")) ||
      "",
    email: userData.email || "",
    phone: userData.phone || "",
    companyName: organisation ? organisation.name || "" : "",
    companyURL: organisation ? organisation.url || "" : "",
    dialCode: ""
  });

  const [inputValues, setInputValues] = useState(generateInputValues());

  useEffect(() => {
    setInputValues(generateInputValues());
    setSelectValues({
      country: organisation ? organisation.country || "" : ""
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  useEffect(() => {
    setInputValues(generateInputValues());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const checkForErrors = () => {
    const number =
      inputValues.phone.length > 6 &&
      (inputValues.phone.includes("+")
        ? phoneUtil.parseAndKeepRawInput(inputValues.phone)
        : phoneUtil.parseAndKeepRawInput("+" + inputValues.phone));

    const isValidNumber =
      inputValues.phone === inputValues.dialCode ||
      (number && phoneUtil.isValidNumber(number));

    const newErrors = {
      firstName:
        inputValues.firstName.length === 0
          ? "Please fill in this field"
          : validateName(inputValues.firstName)
          ? "First name should not contain special characters"
          : "",
      lastName:
        inputValues.lastName.length === 0
          ? "Please fill in this field"
          : validateName(inputValues.lastName)
          ? "Last name should not contain special characters"
          : "",
      phone: !isValidNumber ? "Please enter a valid phone number" : "",
      companyName:
        inputValues.companyName.length === 0 ? "Please fill in this field" : ""
    };

    setErrorValues(newErrors);

    return Object.values(newErrors).some(val => val);
  };

  const [selectValues, setSelectValues] = useState({
    country: organisation ? organisation.country || "" : ""
  });

  const [errorValues, setErrorValues] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    companyName: ""
  });

  const handlePhoneChange = (val, { dialCode }) => {
    setInputValues({ ...inputValues, phone: val, dialCode });
    setSaveDisabled(false);
  };

  const handleChange = e => {
    setInputValues({ ...inputValues, [e.target.name]: e.target.value });
    setSaveDisabled(false);
  };

  const handleSelect = e => {
    setSelectValues({ ...selectValues, [e.target.name]: e.target.value });
    setSaveDisabled(false);
  };

  const { enqueueSnackbar } = useSnackbar();

  const handleUpdate = () => {
    if (checkForErrors()) return;
    setLoading(true);

    const body = {
      ...userData,
      firstName: inputValues.firstName,
      lastName: inputValues.lastName,
      fullName: [inputValues.firstName, inputValues.lastName].join(" "),
      phone:
        inputValues.phone !== inputValues.dialCode ? inputValues.phone : null,
      organisationSite: {
        ...userData.organisationSite,
        organisation: {
          ...userData.organisationSite.organisation,
          name: inputValues.companyName,
          country: selectValues.country,
          url: inputValues.companyURL
        }
      }
    };

    API.patch(`v1/users/${userData.id}`, body)
      .then(res => {
        setSaveDisabled(true);
        setUser(res.data);
        enqueueSnackbar("Profile data successfully updated.");
      })
      .catch(err => {
        if (err && err.status === 400 && err.data && err.data.errors[0]) {
          console.log(err);
          if (err.data.errors[0].field === "user") {
            enqueueSnackbar(err.data.errors[0].defaultMessage);
            setSaveDisabled(true);
          } else {
            setErrorValues({
              [err.data.errors[0].field]: err.data.errors[0].defaultMessage
            });
          }
        } else {
          enqueueSnackbar("Something went wrong, couldn't update profile");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box
      pt={3}
      display="flex"
      flexDirection="column"
      height="100%"
      style={{ boxSizing: "border-box" }}
    >
      {userData.organisationSite ? (
        <div>
          {/* Form */}
          <Form
            data={[
              [
                {
                  renderInputField: true,
                  label: "First name",
                  name: "firstName",
                  type: "text",
                  dataCy: "firstName",
                  maxLength: 50
                },
                {
                  renderInputField: true,
                  label: "Last name",
                  name: "lastName",
                  type: "text",
                  dataCy: "lastName",
                  maxLength: 50
                }
              ],
              [
                {
                  renderInputField: true,
                  label: "Email",
                  name: "email",
                  type: "email",
                  dataCy: "email",
                  helperText: (
                    <Box
                      display="flex"
                      justifyContent="flex-end"
                      component="span"
                    >
                      <RouterLink
                        className={classes.changeEmailLink}
                        to="/account/profile/change-email-address"
                      >
                        Change email address
                      </RouterLink>
                    </Box>
                  ),
                  disabled: true
                }
              ],
              [
                {
                  label: "Phone number (Optional)",
                  name: "phone",
                  componentType: "phone",
                  dataCy: "phone"
                }
              ],
              [
                {
                  labelId: "reg-country",
                  selectId: "reg-country-select",
                  label: "Country",
                  name: "country",
                  options: countries,
                  shrink: true,
                  choose: true,
                  dataCy: "country",
                  onChange: e => handleSelect(e),
                  componentType: "select"
                }
              ],
              [
                {
                  renderInputField: true,
                  label: "Company name",
                  name: "companyName",
                  type: "text",
                  dataCy: "companyName"
                }
              ],
              [
                {
                  renderInputField: true,
                  label: (
                    <span>
                      Company URL{" "}
                      <b>
                        <i>(Optional)</i>
                      </b>
                    </span>
                  ),
                  name: "companyURL",
                  type: "text",
                  dataCy: "companyURL"
                }
              ]
            ]}
            handleChange={handleChange}
            selectValues={selectValues}
            inputValues={inputValues}
            countryCode={organisation.country?.toLowerCase() || "us"}
            errorValues={errorValues}
            handlePhoneChange={handlePhoneChange}
          />
        </div>
      ) : (
        <Box mt={-1}>
          <Skeleton height={28} />
          <Skeleton height={28} />
          <Skeleton height={28} />
        </Box>
      )}
      <Route path="/account/profile/change-email-address">
        <Box
          position="absolute"
          left="0"
          right="0"
          top="0"
          minHeight={"100vh"}
          zIndex={50}
          className={classes.animationWrapper}
        >
          <Box
            className={classes.formWrapper}
            style={{ paddingLeft: drawerExpanded ? 280 : 56 }}
            display="flex"
            flexDirection="column"
            height="100%"
          >
            <ChangeEmail
              userData={userData}
              setLoading={setLoading}
              setUser={setUser}
            />
          </Box>
        </Box>
      </Route>

      {/* Update */}
      <Box display="flex" justifyContent="flex-end" py={1} mt={2.5}>
        <BlackButton
          handleClick={handleUpdate}
          disabled={saveDisabled}
          content="Save"
          data-cy="Save"
        />
      </Box>
    </Box>
  );
}
